
import React from 'react';
import { AlertTriangle, Trash2, X } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { Button } from '../../components/ui/Button';
import { Person } from '../../types';

interface PersonDeleteDialogProps {
  person: Person;
  onClose: () => void;
  onDeleted?: () => void;
}

export const PersonDeleteDialog: React.FC<PersonDeleteDialogProps> = ({ person, onClose, onDeleted }) => {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('people').delete().eq('id', person.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
      if (onDeleted) onDeleted();
      onClose();
    }
  });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-md rounded-lg shadow-2xl overflow-hidden border border-rose-100 animate-in zoom-in-95 duration-200">
        <div className="p-10 text-center space-y-6">
          <div className="w-20 h-20 bg-rose-50 text-rose-500 rounded-full flex items-center justify-center mx-auto shadow-inner border border-rose-100">
            <AlertTriangle size={40} />
          </div>
          
          <div className="space-y-2">
            <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Record Erasure</h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-relaxed">
              Are you certain you wish to permanently wipe the profile for <span className="text-slate-900 font-black">{person.first_name} {person.last_name}</span>? This action is non-reversible.
            </p>
          </div>

          <div className="flex flex-col gap-3 pt-4">
            <Button 
              variant="danger" 
              className="py-4 shadow-lg shadow-rose-100" 
              onClick={() => deleteMutation.mutate()} 
              isLoading={deleteMutation.isPending}
              icon={<Trash2 size={18} />}
            >
              Confirm Erasure
            </Button>
            <Button variant="ghost" onClick={onClose}>Abort Action</Button>
          </div>
        </div>
      </div>
    </div>
  );
};
